/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import {CheckCircleIcon} from '../../icons/CheckCircleIcon';
import {ErrorIcon} from '../../icons/ErrorIcon';
import {InfoIcon} from '../../icons/InfoIcon';
import {MaterialExitCloseIcon} from '../../icons/MaterialExitCloseIcon';
import {NotificationUrgentIcon} from '../../icons/NotificationUrgentIcon';
import Render from '../../render/Render';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import {DLSTheme, IconSize} from '../../types/types';
import {IconButton} from '../buttons/IconButton';

type AlertType = 'info' | 'warning' | 'success' | 'severe' | 'neutral';

export enum Placement {
  right = 'right',
  bottom = 'bottom'
}
type AlertPlacement = `${Placement}`;

interface AlertProps {
  type: AlertType;
  message: string | ReactNode;
  title?: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  icon?: ReactNode;
  form?: ReactNode;
  placement?: AlertPlacement;
  className?: string;
  gutters?: boolean;
  constrained?: boolean;
  show?: boolean;
  onClose?: () => void;
  outlined?: boolean;
  hideSecondaryAction?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  alert : {
    position   : 'relative',
    display    : 'flex',
    alignItems : 'flex-start',
    padding    : ({constrained, hasOnClose}: {constrained?: boolean; hasOnClose?: boolean}) => {
      if (constrained) {
        return hasOnClose ? '8px 36px 8px 12px' : '8px 12px';
      }
      return hasOnClose ? '24px 52px 24px 24px' : '24px';
    },
    borderRadius : '4px',
    margin       : ({gutters}: {gutters?: boolean}) => gutters ? '8px' : '8px 0px'
  },
  icon : {
    height      : '24px',
    marginRight : '12px',
    flexShrink  : 0
  },
  info : {
    backgroundColor : dlsColor('paleBlue'),
    color           : dlsColor('darkTeal'),
    border          : ({outlined}: {outlined?: boolean}) => outlined ? `1px solid ${dlsColor('aaBlue')}` : undefined
  },
  warning : {
    backgroundColor : `${dlsAlt('maps', 'colors', 'lightOrange')}1A`,
    color           : dlsAlt('maps', 'colors', 'chocolate'),
    border          : ({outlined}: {outlined?: boolean}) => outlined ? `1px solid ${dlsColor('caOrange')}` : undefined
  },
  success : {
    backgroundColor : `${dlsColor('caGreen')}2F`,
    color           : dlsAlt('maps', 'colors', 'darkGreen'),
    border          : ({outlined}: {outlined?: boolean}) => outlined ? `1px solid ${dlsColor('caGreen')}` : undefined
  },
  severe : {
    backgroundColor : `${dlsColor('caRed')}1A`,
    color           : dlsAlt('colors', 'darkRed'),
    border          : ({outlined}: {outlined?: boolean}) => outlined ? `1px solid ${dlsAlt('colors', 'darkRed')}` : undefined
  },
  neutral : {
    backgroundColor : `${dlsColor('grey3')}1A`,
    color           : dlsColor('grey1'),
    border          : ({outlined}: {outlined?: boolean}) => outlined ? `1px solid ${dlsColor('grey1')}` : undefined
  },
  title : {
    ...dlsTypography('body3'),
    fontSize     : '16px',
    fontWeight   : 500,
    wordBreak    : 'break-word',
    marginBottom : '8px'
  },
  text : {
    padding    : '2px',
    ...dlsTypography('body3'),
    fontWeight : 400,
    flex       : 1,
    wordBreak  : 'break-word',
    fontSize   : '14px'
  },
  actionRight : {
    ...dlsAlt('typography', 'cta2'),
    display    : 'flex',
    alignItems : 'center',
    padding    : '4px 0 0 16px',
    marginLeft : 'auto',
    gap        : '16px'
  },
  actionBottom : {
    ...dlsAlt('typography', 'cta2'),
    display    : 'flex',
    alignItems : 'flex-start',
    paddingTop : '24px'
  },
  secondaryClass : {
    display        : 'flex',
    justifyContent : 'end'
  },
  closeButton : {
    position : 'absolute',
    top      : '12px',
    right    : '12px'
  }
}));

/**
 * Alert is a reusable component that displays alert messages with predefined styles
 * based on the `type` of alert. It supports different types of alerts such as
 * informational messages, warnings, successes, severe errors, and neutral states.
 * Optionally, a `title` can be displayed in bold above the message.
 *
 * ```typescript
 * import {Alert} from '@ion/dls/src/components/alert/Alert';
 *
 * <Alert type="info" message="This is an informational alert." />
 * <Alert type="warning" title="Warning!" message="This is a warning alert." />
 * <Alert type="success" message="This is a success alert." />
 * <Alert type="severe" title="Critical Error" message="This is a severe alert." />
 * <Alert type="neutral" message="This is a neutral alert." />
 * ```
 *
 * @param type        - The type of alert component.
 * @param message     - The message/alert to display to the user. Required if a title is provided.
 * @param title       - Optional title to display in bold.
 * @param action      - Optional action to display in the alert.
 * @param icon        - Optional icon override. If provided, this icon is rendered instead of the type icon.
 * @param placement   - Optional placement of the action.
 * @param className   - Custom class if provided.
 * @param gutters     - Optional prop to add gutters to the alert.
 * @param constrained - Optional prop to constrain the alert width in order to fit in a smaller space.
 * @param show        - Optional prop to conditionally render the alert. Defaults to true.
 * @param onClose     - Optional callback that renders a close icon button in the alert's upper-right corner.
 */
export const Alert = ({
  type,
  message,
  title,
  action,
  icon,
  form,
  className,
  secondaryAction,
  onClose,
  placement = 'right',
  gutters = true,
  constrained = false,
  show = true,
  outlined = false,
  hideSecondaryAction = false
}: AlertProps) => {
  const classes = useStyles({gutters, constrained, outlined, hasOnClose: !!onClose});

  const icons: Record<AlertType, JSX.Element> = {
    neutral : <InfoIcon size={IconSize.medium} color={dlsColor('grey2')} />,
    info    : <InfoIcon size={IconSize.medium} color={dlsColor('aaBlue')} />,
    success : <CheckCircleIcon size={IconSize.medium} color={dlsColor('caGreen')} />,
    warning : <NotificationUrgentIcon size={IconSize.medium} color={dlsColor('caOrange')} />,
    severe  : <ErrorIcon size={IconSize.medium} color={dlsColor('caRed')} />
  };
  const renderedIcon = icon || icons[type];

  return (
    <Render.If condition={show}>
      <div className={clsx(classes.alert, classes[type], className)}
        tabIndex={-1}
        role="alert"
        aria-label={`${type} alert`}>
        <div className={classes.icon} aria-hidden="true">
          {renderedIcon}
        </div>
        <div className={classes.text}>
          <Render.If condition={title}>
            <div className={classes.title}>{title}</div>
          </Render.If>
          {message}
          {form}
          <div className={secondaryAction ? classes.secondaryClass : ''}>
            <Render.If condition={!hideSecondaryAction && secondaryAction}>
              <div className={classes.actionBottom}>{secondaryAction}</div>
            </Render.If>
            <Render.If condition={action && placement === Placement.bottom}>
              <div className={classes.actionBottom}>{action}</div>
            </Render.If>
          </div>
        </div>
        <Render.If condition={(action && placement === Placement.right) || (!!onClose)}>
          <div className={classes.actionRight}>
            <Render.If condition={action && placement === Placement.right}>
              {action}
            </Render.If>
            <Render.If condition={!!onClose && placement === Placement.right}>
              <IconButton aria-label='Close alert'
                color='currentColor'
                onClick={onClose}
                padding='0px'
                disableHover
                icon={<MaterialExitCloseIcon size={16} />} />
            </Render.If>
          </div>
        </Render.If>
        <Render.If condition={!!onClose && !(placement === Placement.right)}>
          <IconButton className={classes.closeButton}
            aria-label='Close alert'
            color='currentColor'
            onClick={onClose}
            padding='0px'
            disableHover
            icon={<MaterialExitCloseIcon size={16} />} />
        </Render.If>
      </div>
    </Render.If>
  );
};

export default Alert;
